import { type FeatureFlagNode } from 'data-layer';
import { ADDON_ICON_TYPE_MAP } from 'data-layer/constants';
import { type CardAddonsProps } from '@pixum/card';
import { getIconByName } from '../../lib/icons';

/**
 * Builds a `CardAddonsProps['addons']` object from a `FeatureFlagNode`.
 *
 * This is used to map feature flag configuration (typically from CMS like Contentful)
 * into the expected structure for card media addons in the UI.
 *
 * It supports only the `topLeft` addon at the moment, pulling in:
 * - `type`: the badge type (e.g., icon style)
 * - `label`: the text label to display
 * - `icon`: resolved React element from an icon name via `getIconByName()`
 *
 * @param featureFlag - The feature flag data containing addon metadata.
 * @returns A valid `addons` object with `topLeft` filled, or `undefined` if input is invalid.
 *
 */
export function buildIconAddonsFromFeatureFlag(featureFlag?: FeatureFlagNode): CardAddonsProps['addons'] | undefined {
  if (!featureFlag || !featureFlag.topLeft) return undefined;

  const { type, label, iconName } = featureFlag.topLeft;

  return {
    topLeft: {
      type: ADDON_ICON_TYPE_MAP[type],
      label,
      icon: iconName ? getIconByName(iconName) : undefined,
    },
  };
}
